import { apiService } from "api/apiService";
import { applyHelpfulnessVote } from "composables/knowledge-base/helpfulnessVote";
import type {
  FaqCategory,
  FaqCategoryPayload,
  FaqCategoryWithItems,
  FaqItem,
  FaqItemCreate,
  FaqItemUpdate,
} from "models/knowledge-base/faq";
import { defineStore } from "pinia";
import { useQuasar } from "quasar";
import { ref } from "vue";
import { useNotifyStore } from "./notifyStore";

export const useFaqStore = defineStore("faq", () => {
  const loading = ref<boolean>(false);
  const error = ref<string | null>(null);
  const categories = ref<FaqCategoryWithItems[]>([]);
  const selectedCategoryUuid = ref<string | null>(null);
  // The single currently-expanded FAQ item. Only one item is open at a time
  // (matching the public site); also used to auto-open an item from search.
  const openItemUuid = ref<string | null>(null);
  let fetchAllInflight: Promise<FaqCategoryWithItems[]> | null = null;
  const isVoting = new Set<string>();

  const notifyStore = useNotifyStore();
  const { setNotifyError } = notifyStore;
  const $q = useQuasar();

  const fetchAll = async (options?: {
    force?: boolean;
  }): Promise<FaqCategoryWithItems[]> => {
    if (!options?.force && fetchAllInflight) {
      return fetchAllInflight;
    }

    loading.value = true;
    error.value = null;
    fetchAllInflight = apiService
      .get<{ data: FaqCategoryWithItems[] }>(`/api/faq`)
      .then(({ data: { data } }) => {
        categories.value = data;
        return categories.value;
      })
      .catch((e: unknown) => {
        setNotifyError({
          error: e as object,
          fallbackMessage: "Kon FAQ-categorieën en -vragen niet laden",
        });
        throw e;
      })
      .finally(() => {
        loading.value = false;
        fetchAllInflight = null;
      });

    return fetchAllInflight;
  };

  const fetchAllCategories = async (): Promise<FaqCategory[]> => {
    loading.value = true;
    error.value = null;
    try {
      const {
        data: { data },
      } = await apiService.get<{ data: FaqCategory[] }>(`/api/faq-categories`);
      return data;
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon FAQ-categorieën niet laden",
      });
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const createCategory = async (
    payload: FaqCategoryPayload,
  ): Promise<FaqCategory> => {
    error.value = null;
    try {
      const {
        data: { data },
      } = await apiService.post<{ data: FaqCategory }>(
        `/api/faq-categories`,
        payload,
      );
      await fetchAll({ force: true });
      $q.notify({ color: "positive", message: "Categorie aangemaakt" });
      return data;
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon categorie niet aanmaken",
      });
      throw e;
    }
  };

  const updateCategory = async (
    uuid: string,
    payload: FaqCategoryPayload,
  ): Promise<FaqCategory> => {
    error.value = null;
    try {
      const {
        data: { data },
      } = await apiService.put<{ data: FaqCategory }>(
        `/api/faq-categories/${uuid}`,
        payload,
      );
      const category = categories.value.find((c) => c.uuid === uuid);
      if (category) {
        category.title = data.title;
        category.module_ids = data.module_ids;
        category.parent_uuid = data.parent_uuid;
      }
      $q.notify({ color: "positive", message: "Categorie bijgewerkt" });
      return data;
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon categorie niet bijwerken",
      });
      throw e;
    }
  };

  const deleteCategory = async (uuid: string): Promise<void> => {
    error.value = null;
    try {
      await apiService.delete(`/api/faq-categories/${uuid}`);
      const index = categories.value.findIndex((c) => c.uuid === uuid);
      if (index !== -1) {
        categories.value.splice(index, 1);
      }
      $q.notify({ color: "positive", message: "Categorie verwijderd" });
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon categorie niet verwijderen",
      });
      throw e;
    }
  };

  const createItem = async (payload: FaqItemCreate): Promise<FaqItem> => {
    error.value = null;
    try {
      const {
        data: { data },
      } = await apiService.post<{ data: FaqItem }>(`/api/faq-items`, payload);
      await fetchAll({ force: true });
      $q.notify({ color: "positive", message: "Vraag opgeslagen" });
      return data;
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon vraag niet opslaan",
      });
      throw e;
    }
  };

  const updateItem = async (payload: FaqItemUpdate): Promise<FaqItem> => {
    error.value = null;
    try {
      const {
        data: { data },
      } = await apiService.put<{ data: FaqItem }>(
        `/api/faq-items/${payload.uuid}`,
        payload,
      );
      await fetchAll({ force: true });
      $q.notify({ color: "positive", message: "Vraag bijgewerkt" });
      return data;
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon vraag niet bijwerken",
      });
      throw e;
    }
  };

  const deleteItem = async (uuid: string): Promise<void> => {
    error.value = null;
    try {
      await apiService.delete(`/api/faq-items/${uuid}`);
      categories.value.forEach((c) => {
        c.items = c.items.filter((i) => i.uuid !== uuid);
      });
      $q.notify({ color: "positive", message: "Vraag verwijderd" });
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon vraag niet verwijderen",
      });
      throw e;
    }
  };

  const reorder = async (
    payload: Array<{
      category_uuid: string;
      order: number;
      items: Array<{ uuid: string; order: number }>;
    }>,
  ): Promise<void> => {
    error.value = null;
    loading.value = true;
    try {
      await apiService.post(`/api/faq-items/reorder`, { categories: payload });
      await fetchAll({ force: true });
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon volgorde niet opslaan",
      });
      throw e;
    } finally {
      loading.value = false;
    }
  };

  const findItem = (uuid: string): FaqItem | null => {
    for (const category of categories.value) {
      const match = category.items.find((i) => i.uuid === uuid);
      if (match) {
        return match;
      }
    }
    return null;
  };

  const findItemBySlug = (slug: string): FaqItem | null => {
    for (const category of categories.value) {
      const match = category.items.find((i) => i.slug === slug);
      if (match) {
        return match;
      }
    }
    return null;
  };

  const vote = async (uuid: string, helpful: boolean): Promise<void> => {
    // A double-click would otherwise fire a second POST while the first is still
    // in flight: both carry the pre-vote cookie, so the backend counts both.
    if (isVoting.has(uuid)) {
      return;
    }
    isVoting.add(uuid);

    error.value = null;
    try {
      await apiService.post(`/api/faq/${uuid}/vote`, { helpful });
    } catch (e: unknown) {
      setNotifyError({
        error: e as object,
        fallbackMessage: "Kon je stem niet opslaan",
      });
      return;
    } finally {
      isVoting.delete(uuid);
    }

    // The backend answers 204: patch the loaded question so the widget shows the
    // new state without refetching the whole FAQ.
    const item = findItem(uuid);
    if (item) {
      applyHelpfulnessVote(item, helpful);
    }
  };

  return {
    loading,
    error,
    categories,
    selectedCategoryUuid,
    openItemUuid,
    fetchAll,
    fetchAllCategories,
    createCategory,
    updateCategory,
    deleteCategory,
    createItem,
    updateItem,
    deleteItem,
    reorder,
    vote,
    findItem,
    findItemBySlug,
  };
});
